<template>
  <div class="experiment-form">
    <!-- Error Display -->
    <div v-if="errors.length" class="errors-container">
      <b>Please correct the following error(s):</b>
      <ul>
        <li v-for="error in errors" :key="error">{{ error }}</li>
      </ul>
    </div>

    <!-- Section 1: Experiment Info -->
    <CollapsibleSection
      title="Experiment Info"
      icon="fas fa-flask"
      :has-error="!name || !description"
      :default-expanded="true"
    >
      <div class="form-grid">
        <div class="form-group">
          <label for="name">Name <span class="required">*</span></label>
          <input class="form-control" id="name" placeholder="Experiment name" v-model="name"/>
        </div>
        <div class="form-group">
          <label for="description">Description <span class="required">*</span></label>
          <input
            class="form-control"
            id="description"
            placeholder="What does this experiment test?"
            v-model="description"
          />
        </div>
      </div>
    </CollapsibleSection>

    <!-- Section 2: Looker Info -->
    <CollapsibleSection
      title="Looker Info"
      icon="fas fa-chart-line"
      :default-expanded="true"
    >
      <div class="form-grid three-cols looker-grid">
        <div class="form-group">
          <label for="lookerTitle">Looker Title</label>
          <input
            class="form-control"
            id="lookerTitle"
            placeholder="Custom Looker title"
            v-model="lookerTitle"
          />
        </div>
        <div class="form-group">
          <label for="lookerLink">Looker Link</label>
          <input
            class="form-control"
            id="lookerLink"
            placeholder="Auto-generated when the experiment starts"
            v-model="lookerLink"
          />
          <JsonModal
            v-if="isLookerLinkJson"
            :valueObject="lookerLink"
            :disabled="!canEditForm"
            @json-submitted="(val) => lookerLink = val"
            :title="'JSON'"
            :customStyle="{width: '100%'}"
            :useLegacySave="true"
            :editMode="true"
          />
        </div>
        <div
          class="form-group"
          v-for="(folderConfig, folderId) in lookerFolders"
          :key="`folder-link-${folderId}`"
        >
          <label class="label-with-tooltip" :for="`folderLink-${folderId}`">
            <span>{{ folderLabel(folderId, folderConfig) }} Source Look Link</span>
            <Tooltip
              class="field-tooltip"
              :content="'The new experiment look in the ' + folderLabel(folderId, folderConfig) + ' folder will be copied from this Looker link. If empty, the assigned template look is used; if both are empty, the latest look in the folder is used.'"
            />
          </label>
          <input
            class="form-control"
            :id="`folderLink-${folderId}`"
            placeholder="Source Looker link to copy from (optional)"
            :value="folderLookLinkValue(folderId, folderConfig)"
            @input="setFolderLookLink(folderId, folderConfig, $event.target.value)"
            :disabled="!folderLinksEditable"
          />
        </div>
      </div>
    </CollapsibleSection>

    <!-- Section 3: Experiment Configuration -->
    <CollapsibleSection
      title="Experiment Configuration"
      icon="fas fa-cog"
      :has-error="!audience || !percentage"
      :default-expanded="true"
    >
      <div class="form-grid three-cols">
        <div class="form-group">
          <label for="category">Category</label>
          <select v-model="category" class="form-control" :disabled="!canEditForm" id="category">
            <option disabled value="">Select category</option>
            <option
              v-for="cat in categories"
              :key="`category-${cat}`"
              :value="cat"
            >
              {{ cat }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label for="audience">Audience <span class="required">*</span></label>
          <select v-model="audience" class="form-control" :disabled="!canEditForm" id="audience">
            <option disabled value="">Select audience</option>
            <option
              v-for="aud in audiences"
              :key="`audience-${aud.id}`"
              :value="aud.id"
            >
              {{ aud.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label for="percentage">Percentage <span class="required">*</span></label>
          <input
            class="form-control"
            id="percentage"
            placeholder="Percentage"
            v-model.number="percentage"
            type="number"
            :disabled="experimentEnded"
          />
        </div>
      </div>

      <!-- Checkboxes Row -->
      <div class="checkbox-row">
        <label class="checkbox-item only-new-users" :class="{ active: onlyNewUsers }">
          <input type="checkbox" v-model="onlyNewUsers">
          <span>Only new users</span>
        </label>
      </div>
    </CollapsibleSection>

    <!-- Variants Section -->
    <div class="variants-section">
      <div class="variants-header">
        <i class="fas fa-layer-group"></i>
        <h6>Variants</h6>
        <button v-if="canEditForm" class="btn btn-sm btn-add-variant" @click="addVariant()">
          <i class="fas fa-plus"></i> Add Variant
        </button>
      </div>
      <div class="variants-content">
        <div class="form-group">
      <div class="row mb-1 variant-item" v-for="(variant, varIndex) in variants" :key="`variant-${varIndex}`">
        <input
            class="form-control col-2"
            :id="'_variant_name_' + varIndex"
            placeholder="Name"
            v-model="variant._variant_name"
            :disabled="!canEditForm"
            style="margin: 10px 0;"
        />
        <div class="col-10">
          <p v-if="keys.length > 0">Parameters:</p>
          <div class="row" v-for="(key,keyIndex) in keys" :key="`var-parameter-${varIndex}-${key}`" style="margin: 10px 0;">
            <div class="col-5" v-if="varIndex == 0">
              <select
                v-model="keys[keyIndex]"
                class="form-control"
                @change="keySelected(keyIndex)"
                :disabled="!canEditForm"
                id="select-parameter"
              >
                <option disabled value="">Please select a key</option>
                <option
                  v-for="parameter in parameters"
                  :title="parameter.desc"
                  :key="`parameterKeyOption-${keyIndex}-${parameter.key}`"
                >
                  {{ parameter.key }}
                </option>
              </select>
              <div class="send-toggle-row">
                <span class="send-toggle-text">Send parameter</span>
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    :checked="!variantDoNotSend[`${varIndex}-${key}`]"
                    @change="toggleVariantDoNotSend(varIndex, key)"
                    :disabled="!canEditForm"
                  >
                  <span class="toggle-slider"></span>
                </label>
                <Tooltip v-if="keyIndex === 0" :content="'When disabled, this parameter will not be included in the Config API response if the user is assigned to this variant.'"/>
              </div>
            </div>
            <div class="col-5 my-auto" style="color:black;" v-else>
              {{ key }}:
              <div class="send-toggle-row">
                <span class="send-toggle-text">Send parameter</span>
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    :checked="!variantDoNotSend[`${varIndex}-${key}`]"
                    @change="toggleVariantDoNotSend(varIndex, key)"
                    :disabled="!canEditForm"
                  >
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div class="col-5">
              <div
                v-if="variantDoNotSend[`${varIndex}-${key}`]"
                class="variant-value-not-sent"
              >
                <span class="not-sent-badge">
                  <i class="fas fa-ban"></i> Not sent to client
                </span>
              </div>
              <template v-else>
                <input
                  class="form-control"
                  :id="`variant-${varIndex}-${key}-value`"
                  placeholder="Value"
                  v-model="variant[key]"
                  :disabled="!canEditForm"
                />
                <JsonModal
                  :valueObject="variant[key]"
                  :disabled="!canEditForm"
                  @json-submitted="(val)=> setVariantParamValue(varIndex,key,val)"
                  :title="'JSON'"
                  :customStyle="{width: '100%'}"
                  :variantName="variant._variant_name"
                  :parameterName="key"
                  :audienceName="audienceName"
                  :audience="audienceObject"
                  :useLegacySave="true"
                  :editMode="true"
                />
              </template>
            </div>
            <div class="col-2" v-if="varIndex == 0">
              <button v-if="canEditForm" class="btn btn-sm btn-danger" @click="removeKey(keyIndex)">-</button>
            </div>
            <div class="col-2" v-if="varIndex > 0 && keyIndex == 0">
              <button v-if="!experimentEnded" class="btn btn-sm btn-danger" :disabled="!canEditForm" @click="removeVariant(varIndex)" >X</button>
            </div>
          </div>
          <p v-if="isls.length > 0">Isl Keys:</p>
          <div class="row" v-for="(key,keyIndex) in isls" :key="`var-isl-${varIndex}-${key}`" style="margin: 10px 0;">
            <div class="col-5" v-if="varIndex == 0">
              <select
                v-model="isls[keyIndex]"
                class="form-control"
                @change="islKeySelected(keyIndex)"
                :disabled="!canEditForm"
              >
                <option disabled value="">Please select a key</option>
                <option
                  v-for="islKey in islKeys"
                  :title="islKey.desc"
                  :key="`parameterKeyOption-${keyIndex}-${islKey.key}`"
                >
                  {{ islKey.key }}
                </option>
              </select>
            </div>
            <div class="col-5 my-auto" style="color:black;" v-else>
              {{ key }} :
            </div>
            <div class="col-4" v-if="varIndex == 0">
              <button v-if="canEditForm" class="btn btn-sm btn-danger" @click="removeIslKey(keyIndex)">Remove lang</button>
              <button @click="addNewLanguage(keyIndex)" class="btn btn-sm btn-info" :disabled="!canEditForm">Add lang</button>
            </div>
            <div class="col-2" v-if="varIndex > 0 && keyIndex == 0">
              <button v-if="!experimentEnded" class="btn btn-sm btn-danger" :disabled="!canEditForm" @click="removeVariant(varIndex)" >X</button>
            </div>
            <div class="row" v-for="(language, languageIndex) in selectedLanguagesByKeyIndex[keyIndex] " :key="`${varIndex}-${key}-${language}-${languageIndex}`">
              <div class="col-4" style="margin-top: 5px;">
                <select 
                v-model="selectedLanguagesByKeyIndex[keyIndex][languageIndex]" 
                class="form-control" 
                style="margin-left: 15px;" 
                :disabled="!canEditForm">
                  <option disabled value="">Please select a language</option>
                  <option
                    v-for="subLanguage in languageOptions"
                    :key="'typeSelect-' + subLanguage.code"
                    :value="subLanguage.code"
                  >
                    {{ subLanguage.name }}
                  </option>
                </select>
              </div>
              <div class="col-6" style="margin-top: 5px;">             
                <textarea
                class="form-control text-field-control"
                :id="`${varIndex}-${key}-${language}`"
                placeholder="Value"
                :disabled="!canEditForm"
                v-model="variants[varIndex].isl_keys[key][language]" 
              />
              </div>
                <button @click="removeLanguage(languageIndex, keyIndex)" :disabled="!canAccess" class="btn btn-sm btn-warning">- Lang</button>
            </div>
          </div>
          <div class="row" v-if="varIndex == 0">
            <p class="text-center mx-auto">
              <button v-if="canEditForm" class="btn btn-sm btn-info add-variant-key-button" @click="addParameter()">Add parameter</button>
              &nbsp;
              <button v-if="canEditForm" class="btn btn-sm btn-info add-variant-key-button" @click="addIslKey()">Add isl key</button>
            </p>
          </div>

        </div>
      </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";
import JsonModal from "./common/JsonModal.vue";
import CollapsibleSection from "./common/CollapsibleSection.vue";
import Tooltip from "./common/Tooltip.vue";
import { permissionsMixin } from '../mixins/permissions.mixin';
import { isValidLookerLink } from '../util/lookerLinkHelper';
import notify from '../util/notification';

export default {
  mixins: [permissionsMixin],
  props: {
    experiment: Object,
    viewOnly: {
      type: Boolean,
      default: false
    },
    experimentEnded: {
      type: Boolean,
      default: false
    }
  },
  components: {
    JsonModal,
    CollapsibleSection,
    Tooltip
  },
  created(){
    this.$parent.$on('createExperiment', this.createExperiment)
    this.$parent.$on('editExperiment', this.editExperiment)
    // put the necessary values if this is an edit view
    if (this.experiment){
      console.log(this.experiment)
      this.name = this.experiment.name
      this.description = this.experiment.description
      this.audience = this.experiment.audience
      this.percentage = this.experiment.percentage
      this.keys = [...this.experiment.keys]
      this.status = this.experiment.status
      this.isls = [...(this.experiment.isls || [])]
      this.folderLookLinks = { ...(this.experiment.folder_look_links || {}) }
      // Legacy single copy link: surface it on the experiments folder input so it
      // stays visible/editable; saving an edit migrates it into folder_look_links.
      const experimentsFolderId = this.folderIdForName('experiments')
      const experimentsFolderKey = experimentsFolderId || 'experiments'
      if (this.experiment.copy_experiment_link && !this.folderLookLinks[experimentsFolderKey]) {
        this.$set(this.folderLookLinks, experimentsFolderKey, this.experiment.copy_experiment_link)
      }
      this.lookerTitle = this.experiment.looker_title || ""
      this.lookerLink = typeof this.experiment.looker_link === 'object' && this.experiment.looker_link !== null
        ? JSON.stringify(this.experiment.looker_link, null, 2)
        : (this.experiment.looker_link || '')
      this.category = this.experiment.category
      this.onlyNewUsers = this.experiment.only_new_users
      this.isPaused = this.experiment.is_paused
      this.variants = this.experiment.variants.map((variant, index) => {
        const variantObject = { 
          _variant_name: String.fromCharCode(65 + index % 26) + (index >= 26 ? Math.floor(index / 26) : ''), 
          isl_keys: {},
          ...cloneDeep(variant) }
        this.keys.forEach(key => {
          if (variantObject[key] === null) {
            this.$set(this.variantDoNotSend, `${index}-${key}`, true);
          }
        });
        return variantObject
      })
      let islIndex = 0
      for (let key in this.variants[0].isl_keys){
        for (let lang in this.variants[0].isl_keys[key]){
          if(!this.selectedLanguagesByKeyIndex[islIndex]) this.selectedLanguagesByKeyIndex[islIndex] = []
          this.selectedLanguagesByKeyIndex[islIndex].push(lang)
        }
        islIndex += 1
      }
  }
  },
  data() {
    return {
      name: "",
      description: "",
      audience: undefined,
      percentage: undefined,
      keys: [],
      isls: [],
      selectedLanguagesByKeyIndex : [],
      onlyNewUsers: true,
      isPaused: false,
      variants: [{ _variant_name : "A", isl_keys: {}}],
      errors: [],
      categories: ["Monetization", "Pricing", "Paywall"],
      category: "-",
      lookerLink: "",
      folderLookLinks: {},
      lookerTitle: "",
      status: undefined,
      skipImages: false,
      variantDoNotSend: {}
    };
  },
  computed: {
    canEditForm(){
      if (this.viewOnly) {
        // If in view-only mode, check if user can edit this specific experiment
        return this.canEditThisExperiment(this.experiment);
      }
      return true; // Allow editing in create mode
    },

    lookerFolders() {
      return this.$store.getters.lookerConfiguration.folders || {};
    },
    folderLinksEditable() {
      // Looks are generated once, on created -> started; links are frozen after start
      return this.canEditForm && (!this.experiment || this.experiment.status === 'created');
    },
    trimmedFolderLookLinks() {
      // Non-empty entries only; includes stored keys of folders no longer in the config
      const result = {};
      Object.entries(this.folderLookLinks).forEach(([folderKey, link]) => {
        const trimmed = typeof link === 'string' ? link.trim() : '';
        if (trimmed) result[this.folderIdForKey(folderKey)] = trimmed;
      });
      return result;
    },

    languageOptions() {
      const audienceOptions = this.$store.getters.audienceOptions;
      if (audienceOptions) {
        const languageKey = audienceOptions.find(
          (item) => item.key == "language"
        );
        if (languageKey) {
          return languageKey.options;
        } else {
          return [];
        }
      } else {
        return [];
      }
    },
    audiences() {
      const audiences = this.$store.getters.audiences.filter(audience => audience.percentage == 100);
      return audiences.sort((a, b) => a.name.localeCompare(b.name))
    },
    parameters() {
      return this.$store.getters.parameters.sort((a, b) => {
        return a.key > b.key;
      })
    },
    islKeys() {
      return this.$store.getters.islKeys.sort((a, b) => {
        return a.key > b.key;
      })
    },
    audienceName() {
      if (!this.audience) return '';
      const aud = this.audiences.find(a => a.id === this.audience);
      return aud ? aud.name : '';
    },
    audienceObject() {
      if (!this.audience || this.audiences) return null;
      return this.audiences.find(a => a.id === this.audience) || null;
    },
    isLookerLinkJson() {
      if (!this.lookerLink || typeof this.lookerLink !== 'string') return false;
      try {
        const parsed = JSON.parse(this.lookerLink);
        return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed);
      } catch {
        return false;
      }
    }
  },
  methods: {
    parseLookerLink(val) {
      if (!val || typeof val !== 'string') return val;
      try {
        const parsed = JSON.parse(val);
        if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // not valid JSON, return as string
      }
      return val;
    },
    createExperiment(index) {
      this.errors = [];    
      if (!this.name) {
        this.errors.push('Experiment name required!');
      }
      if (!this.description) {
        this.errors.push('Experiment description required!');
      }
      if (!this.percentage) {
        this.errors.push('Percentage required!');
      }
      if(!this.audience){
        this.errors.push('Experiment audience required!')
      }
      if(this.isls.length > 0 && this.selectedLanguagesByKeyIndex[0].length == 0) 
      this.errors.push('Please select at least one language for isl key')

      // Validate per-folder look links if provided
      Object.entries(this.trimmedFolderLookLinks).forEach(([folderKey, link]) => {
        if (!this.isValidLookerLink(link)) {
          this.errors.push(`${this.folderLabel(folderKey, this.lookerFolders[folderKey])} Source Look Link must be a valid Looker URL`);
        }
      });

      // Validate looker link JSON syntax if it looks like JSON
      if (this.lookerLink && typeof this.lookerLink === 'string' && this.lookerLink.trim().startsWith('{')) {
        try {
          JSON.parse(this.lookerLink);
        } catch {
          this.errors.push('Looker Link contains invalid JSON');
        }
      }

      this.variants.forEach(variant => {
        if (Object.keys(variant).length <3 && this.isls.length <1) {
          this.errors.push('Please add Isl key or parameter for variant ' + variant._variant_name)
        }
        this.keys.forEach(key => {
          if (variant[key] === null) return;
          if(variant[key] == "" || variant[key]=== undefined){
            if (!
              confirm(
                `You created an experiment without filling ${variant._variant_name} variant parameter value, do you want to continue ?`
              )
            ){
              this.errors.push('Please fill all the parameter values for variant ' + variant._variant_name)
            }
          }
        })
      })
      for(let varIndex = 0; varIndex< this.variants.length; varIndex ++){
        let islOrder = 0
        this.isls.forEach(isl => {
          this.variants[varIndex].isl_keys[isl] = {}
          const arr = this.selectedLanguagesByKeyIndex[islOrder]
          arr.forEach(language => {
            if(document.getElementById(`${varIndex}-${isl}-${language}`).value)
            this.variants[varIndex].isl_keys[isl][language] = document.getElementById(`${varIndex}-${isl}-${language}`).value
            else this.errors.push(`${language} language value is required for ${varIndex + 1}. variant` )
          })
          islOrder += 1
        })
      }
      if(this.errors.length === 0){
        const payload = {
        name: this.name,
        description: this.description,
        start_date: new Date(this.start_date) * 1,
        end_date: new Date(this.end_date) * 1,
        pause_date: new Date(this.pause_date) * 1,
        audience: this.audience,
        percentage: Number(this.percentage),
        keys: this.keys,
        isls: this.isls,
        variants: this.variants,
        status: "created",
        create_date: Date.now(),
        only_new_users: this.onlyNewUsers,
        is_paused: this.isPaused,
        category: this.category,
        looker_link: this.parseLookerLink(this.lookerLink),
        folder_look_links: this.trimmedFolderLookLinks,
        looker_title: this.lookerTitle,
        skip_images: this.skipImages
      };
      this.$store.dispatch('configAdd', {type: 'experiment', payload});
      document.getElementById('close').click(); 
      }

    },
    async editExperiment(index) {
      // Validate JSON values in variants
      const invalidJsonErrors = this.validateVariantJsonValues();
      if (invalidJsonErrors.length > 0) {
        notify(`Invalid JSON detected:\n${invalidJsonErrors.join('\n')}`, 'error');
        this.$emit('edit-cancelled');
        return;
      }
      
      const payload = {
        id: this.experiment.id,
        data: {
          name: this.name,
          description: this.description,
          audience: this.audience,
          percentage: Number(this.percentage),
          keys: this.keys,
          isls: this.isls,
          variants: this.variants,
          status: this.experiment.status,
          create_date: this.experiment.create_date,
          start_date: this.experiment.start_date,
          pause_date: this.experiment.pause_date,
          last_update: Date.now(),
          only_new_users: this.onlyNewUsers,
          is_paused: this.isPaused,
          looker_link: this.parseLookerLink(this.lookerLink),
          category: this.category,
          folder_look_links: this.trimmedFolderLookLinks,
          looker_title: this.lookerTitle,
          skip_images: this.experiment.skip_images || false
        }
      };
      try {
        // Check if this is an ongoing experiment and user has permission
        if(this.experiment.status === "started" && !this.experiment.is_paused) {
          if (!this.canEditOngoingExperiments) {
            alert("You don't have permission to edit ongoing experiments. Please contact an administrator.");
            this.$emit('edit-cancelled');
            return;
          }
          
          // Show warning for ongoing experiments
          const confirmed = window.confirm("You are about to edit an ongoing experiment. This may affect the experiment results. Are you sure?");
          if (!confirmed) {
            this.$emit('edit-cancelled');
            return;
          }
        }
        
        await this.$store.dispatch('configEdit', {type: 'experiment', payload});
        this.$emit('edit-successful');
      } catch (error) {
        console.error('Error editing experiment:', error);
        this.$emit('edit-fail');
      }
      
    },
    addVariant() {
      const newVar = {}
      this.keys.forEach(key => {
        if (key)
        newVar[key] = ""
      })
      newVar.isl_keys = {}
      let keyIndex = 0
      this.isls.forEach(key => {
        if (key)
        newVar.isl_keys[key] = {}
        this.selectedLanguagesByKeyIndex[keyIndex].forEach(lang => {
          newVar.isl_keys[key][lang] = ""
        })
        keyIndex += 1 
      })
      const varIndex = this.variants.length
      newVar._variant_name = String.fromCharCode(65 + varIndex % 26) + (varIndex >= 26 ? Math.floor(varIndex / 26) : '')
      this.variants.push(newVar)
      this.$emit('variants-change', this.variants.length)
    },
    removeVariant(index) {
      this.variants.splice(index,1)
      this.$emit('variants-change', this.variants.length)
    },

setVariantParamValue(varIndex,key,val){
      console.log(this.variants[varIndex], key)
      const tempVariant = this.variants[varIndex]
      tempVariant[key] = val
      {this.$set(this.variants, varIndex, tempVariant)}
      console.log(this.variants[varIndex])
    },
    validateVariantJsonValues() {
      const errors = [];
      
      this.variants.forEach((variant, varIndex) => {
        const variantName = variant._variant_name || `Variant ${varIndex + 1}`;
        
        this.keys.forEach(key => {
          if (!key) return;
          
          const value = variant[key];
          if (typeof value !== 'string') return;
          
          const trimmedValue = value.trim();
          // Check if value looks like JSON (starts with { or [)
          if (trimmedValue.startsWith('{') || trimmedValue.startsWith('[')) {
            try {
              JSON.parse(trimmedValue);
            } catch (e) {
              errors.push(`${variantName} - "${key}": Invalid JSON syntax`);
            }
          }
        });
      });
      
      return errors;
    },
    addParameter() {
      this.keys.push(undefined)
    },
    addNewLanguage(keyIndex){
      if(!this.selectedLanguagesByKeyIndex[keyIndex]) this.selectedLanguagesByKeyIndex[keyIndex]  = []
      const arr = this.selectedLanguagesByKeyIndex[keyIndex]
      arr.push(undefined)
      this.$set(this.selectedLanguagesByKeyIndex, keyIndex, arr)
      console.log(this.selectedLanguagesByKeyIndex)
    },
    removeLanguage(index,keyIndex){
      this.selectedLanguagesByKeyIndex[keyIndex].splice(index,1)
    },
    addIslKey() {
      this.isls.push(undefined)
    },
    removeKey(index) {
      const removedKey = this.keys[index]
      this.keys.splice(index,1)
      this.variants.forEach(variant => {
        delete variant[removedKey]
      })
    },
    removeIslKey(index) {
      const removedKey = this.isls[index]
      this.isls.splice(index,1)
      this.variants.forEach(variant => {
        delete variant.isl_keys[removedKey]
      })
    },
    keySelected(index){
      const key = this.keys[index]
      if (key){
        this.variants.forEach(variant => {
          variant[key] = ""
          for (let varKey in variant){
            if (this.keys.indexOf(varKey) < 0 && varKey !== "_variant_name"  && varKey !== "isl_keys") {
              delete variant[varKey]
            }
          }
        })
      }
    },
    islKeySelected(index){
      const key = this.isls[index]
      if (key){
        this.variants.forEach(variant => {
          this.$set(variant.isl_keys, key, {})
          for (let varKey in variant.isl_keys){
            if (this.isls.indexOf(varKey) < 0) {
              delete variant.isl_keys[varKey]
            }
          }
        })
      }
    },
    toggleVariantDoNotSend(varIndex, key) {
      const stateKey = `${varIndex}-${key}`;
      const isChecked = !this.variantDoNotSend[stateKey];
      this.$set(this.variantDoNotSend, stateKey, isChecked);
      if (isChecked) {
        this.$set(this.variants[varIndex], key, null);
      } else {
        this.$set(this.variants[varIndex], key, '');
      }
    },
    folderLabel(folderId, folderConfig) {
      return folderConfig && typeof folderConfig === 'object' && folderConfig.folder_name
        ? folderConfig.folder_name
        : folderId;
    },
    folderIdForName(folderName) {
      const match = Object.entries(this.lookerFolders).find(([, folderConfig]) => this.folderLabel('', folderConfig) === folderName);
      return match ? match[0] : '';
    },
    folderIdForKey(folderKey) {
      if (this.lookerFolders[folderKey]) {
        return folderKey;
      }
      return this.folderIdForName(folderKey) || folderKey;
    },
    folderLookLinkValue(folderId, folderConfig) {
      const folderName = this.folderLabel(folderId, folderConfig);
      return this.folderLookLinks[folderId] || this.folderLookLinks[folderName] || '';
    },
    setFolderLookLink(folderId, folderConfig, value) {
      const folderName = this.folderLabel(folderId, folderConfig);
      if (folderName !== folderId && this.folderLookLinks[folderName] !== undefined) {
        this.$delete(this.folderLookLinks, folderName)
      }
      this.$set(this.folderLookLinks, folderId, value)
    },
    isValidLookerLink
  }
};
</script>

<style scoped>
  .experiment-form {
    padding: 0.5rem;
  }

  /* Error display */
  .errors-container {
    background: #f8d7da;
    border: 1px solid #f5c6cb;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1rem;
    color: #721c24;
  }

  .errors-container b {
    color: #721c24;
  }

  .errors-container ul {
    margin-bottom: 0;
    padding-left: 1.25rem;
  }

  label {
    color: #000;
  }

  input, select, input:disabled, select:disabled {
    color: #000 !important;
    background-color: #fff;
  }

  /* Required indicator */
  .required {
    color: #e74c3c;
  }

  /* Form grid layouts */
  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .form-grid.three-cols {
    grid-template-columns: repeat(3, 1fr);
  }

  .looker-grid {
    align-items: start;
  }

  .label-with-tooltip {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
  }

  .label-with-tooltip span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .field-tooltip {
    flex: 0 0 auto;
    margin-inline: 0;
  }

  @media (max-width: 768px) {
    .form-grid,
    .form-grid.three-cols {
      grid-template-columns: 1fr;
    }
  }

  .form-group {
    margin-bottom: 0;
  }

  /* Checkbox row */
  .checkbox-row {
    display: flex;
    gap: 2rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #dee2e6;
    flex-wrap: wrap;
  }

  .checkbox-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    cursor: pointer;
  }

  .checkbox-item input[type="checkbox"] {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    accent-color: #28a745;
  }

  /* Only new users checkbox styling */
  .checkbox-item.only-new-users {
    padding: 0.75rem 1rem;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .checkbox-item.only-new-users span {
    font-weight: 600;
    color: #856404;
  }

  .checkbox-item.only-new-users.active {
    background: #d4edda;
    border-color: #28a745;
  }

  .checkbox-item.only-new-users.active span {
    color: #155724;
  }

  /* Variants section */
  .variants-section {
    margin-top: 1rem;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: visible;
  }

  .variants-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: #f8f9fa;
    border-bottom: 2px solid #3498db;
  }

  .variants-header h6 {
    margin: 0;
    font-weight: 600;
    color: #2c3e50;
  }

  .variants-header i {
    color: #3498db;
  }

  .btn-add-variant {
    margin-left: auto;
    background: #3498db;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }

  .btn-add-variant:hover {
    background: #2980b9;
    color: #fff;
  }

  .variants-content {
    padding: 1rem;
  }

  .conditionDiv {
    color: #000 !important;
    background-color: #ecf0f1;
    border-radius: 10px;
  }

  .variant-item {
    background: rgba(0,0,0,0.05);
    margin: 10px 5px;
    padding: 5px;
    box-sizing: border-box;
    border-radius: 10px;
  }

  .add-variant-key-button {
    margin: 15px 0 5px;
  }

  .text-field-control {
    width: 100%;
    min-height: 100px;
    resize: vertical;
    color: black;
    border: 1px solid #000 !important;
    padding: 10px;
    box-sizing: border-box;
    border-radius: 10px;
  }

  .send-toggle-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .send-toggle-text {
    font-size: 0.75rem;
    font-weight: 500;
    color: #555;
  }

  .toggle-switch {
    position: relative;
    display: inline-block;
    width: 32px;
    height: 18px;
    flex-shrink: 0;
    margin: 0;
    cursor: pointer;
  }

  .toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .toggle-slider {
    position: absolute;
    inset: 0;
    background-color: #ccc;
    border-radius: 18px;
    transition: background-color 0.2s ease;
  }

  .toggle-slider::before {
    content: '';
    position: absolute;
    height: 12px;
    width: 12px;
    left: 3px;
    bottom: 3px;
    background-color: #fff;
    border-radius: 50%;
    transition: transform 0.2s ease;
  }

  .toggle-switch input:checked + .toggle-slider {
    background-color: #28a745;
  }

  .toggle-switch input:checked + .toggle-slider::before {
    transform: translateX(14px);
  }

  .variant-value-not-sent {
    display: flex;
    align-items: center;
    min-height: calc(1.5em + 0.75rem + 2px);
    padding: 0.375rem 0.75rem;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    background-color: #e9ecef;
  }

  .not-sent-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.25rem 0.5rem;
    background: rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    color: #6c757d;
    font-size: 0.8rem;
    font-style: italic;
  }

  .not-sent-badge i {
    font-size: 0.7rem;
  }
</style>
